import React from 'react';
import $ from 'jquery';
import cn from 'classnames';

import BrButton from '@components/BrButton';
import BrTooltip from '@components/BrTooltip';
import BrIcons from '@components/BrIcons';
import ColorStyles from '@components/ColorStyles';
import { DomUtils } from '@components/UtilComponents/UtilFunctions';
import * as HeaderFilters from './HeaderFilters';
import BRTABLE_CONSTANTS from './Constants';
import './header.scss';

const getHeaderTitle = (col) => (
  <BrTooltip className="header-tootlip" location={BrTooltip.Locations.bottom} isDark>
    {col.displayName || col.key}
    {col.tooltip && <BrTooltip.Tooltip>{col.tooltip}</BrTooltip.Tooltip>}
  </BrTooltip>
);

const getSortIcon = (orderBy) => {
  switch (orderBy) {
    case BRTABLE_CONSTANTS.ASC:
      return <BrIcons.SortAsc />;
    case BRTABLE_CONSTANTS.DESC:
      return <BrIcons.SortDesc />;
  }
  return <BrIcons.SortInactive />;
};

const renderFilter = (col) => {
  switch (col.type) {
    case 'string':
    case 'date':
      return HeaderFilters.stringFilter();
    case 'number':
      return HeaderFilters.numberFilter();
    case 'checkboxEnum':
      return HeaderFilters.checkboxFilter(col.filterValues);
  }
  return null;
};

const handleReset = (e, col, closeByBlur, onSetFilter) => {
  $(e.target).closest('.filter-holder').find('.filter-form')[0].reset();
  onSetFilter(col.key);
  closeByBlur(e);
};

const handleSubmit = (e, col, closeByBlur, onSetFilter) => {
  const data = $(e.target)
    .closest('.filter-holder')
    .find('.filter-form')
    .serializeArray()
    .reduce((pv, obj) => ({ ...pv, [obj.name]: obj.value }), {});
  if (
    $.isEmptyObject(data)
      || (typeof data.text === 'string' && !data.text.trim())
      || (typeof data.value1 === 'string' && !data.value1.trim())
      || data.all === 'on'
  ) {
    // handle scenarios where values are set to non -> reset the filter
    onSetFilter(col.key);
  } else {
    onSetFilter(col.key, data);
  }
  $(e.target).find('input').blur();
  e.preventDefault();
  closeByBlur(e);
};

const advanceSortOptions = (col, handleSortColumn, sortState, onSetFilter) => {
  const closeByBlur = (e) => $(e.target).closest('.advance-sort-holder').blur();
  return (
    <div data-id={`brtable-colsort-box-${col.key}`} className="sort-popup">
      <div data-id={`brtable-colsort-options-${col.key}`} className="sort-options">
        <p
          data-id={`brtable-colsort-options-asc-${col.key}`}
          className={sortState === BRTABLE_CONSTANTS.ASC ? 'on' : undefined}
          onClick={(e) => {
            handleSortColumn(col.key, BRTABLE_CONSTANTS.ASC);
            closeByBlur(e);
          }}
          role="presentation"
        >
          Sort Asc
        </p>
        <p
          data-id={`brtable-colsort-options-desc-${col.key}`}
          className={sortState === BRTABLE_CONSTANTS.DESC ? 'on' : undefined}
          onClick={(e) => {
            handleSortColumn(col.key, BRTABLE_CONSTANTS.DESC);
            closeByBlur(e);
          }}
          role="presentation"
        >
          Sort Desc
        </p>
      </div>
      {col.isFilterable && (
        <div className="filter-holder">
          <div className="filter-title">FILTER</div>
          <div className="filter-box">
            <form
              data-id={`brtable-colsort-filter-form-${col.key}`}
              className="filter-form"
              onSubmit={(e) => handleSubmit(e, col, closeByBlur, onSetFilter)}
            >
              {renderFilter(col)}
            </form>
          </div>
          <div className="filter-btns">
            <BrButton
              className="filter-confirmation-btn"
              title="Apply"
              colorStyle={ColorStyles.BGBLUE}
              onClick={(e) => handleSubmit(e, col, closeByBlur, onSetFilter)}
            />
            <BrButton
              className="filter-confirmation-btn"
              title="Reset"
              colorStyle={ColorStyles.BGGRAY}
              onClick={(e) => handleReset(e, col, closeByBlur, onSetFilter)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const closeAndReset = (sortPopupDiv) => {
  const target = sortPopupDiv[0]
    ? sortPopupDiv
    : sortPopupDiv.prevObject.closest('.advance-sort-holder').find('.sort-popup');
  if (target) {
    target.hide();
    target.closest('.advance-sort-holder').toggleClass('on-select', false);
  }
};

const openAndFill = (cell, filterValues, handleClose) => {
  const popup = $(cell).closest('.advance-sort-holder').find('.sort-popup');
  if (!popup.is(':visible')) {
    if (filterValues && !$.isEmptyObject(filterValues)) {
      for (const key in filterValues) {
        const field = popup.find(`[name="${key}"]`);
        if (field.is(':checkbox')) {
          field.prop('checked', filterValues[key]);
        } else {
          field.val(filterValues[key]);
        }
      }
    } else {
      const filterForm = popup.find('.filter-form');
      if (filterForm.length) {
        filterForm[0].reset();
      }
    }
    popup.show();
    popup.closest('.advance-sort-holder').toggleClass('on-select', true);
  } else {
    handleClose();
  }
};

export default function HeaderRenderer(col, sortState, filterStates, onSortBy, onSetFilter) {
  const handlePopupBlur = DomUtils.genBlurHandler('sort-popup', closeAndReset, 'ReactVirtualized__Grid');
  const { background, titleAlign, isSortable, isAdvanceSort } = col;
  const classname = cn('br-table-header-cell', background, titleAlign, {
    'is-sortable': isSortable,
    'is-advance-sort': isAdvanceSort,
  });
  const title = getHeaderTitle(col);
  return col.isSortable ? (
    <div className={classname} onClick={() => onSortBy(col.key)}>
      {title}
      <div className="flexdiv" />
      {getSortIcon(sortState)}
    </div>
  ) : col.isAdvanceSort ? (
    <div tabIndex="1" className="br-table-header-cell advance-sort-holder" onBlur={handlePopupBlur}>
      <div
        className={classname}
        onClick={(e) => {
          openAndFill(e.target, filterStates[col.key], () => handlePopupBlur(e));
        }}
      >
        {title}
        {getSortIcon(sortState)}
        {filterStates[col.key] && <BrIcons.Filter />}
        <BrIcons.DownCaret className="advance-sort-toggle" />
      </div>
      {advanceSortOptions(col, onSortBy, sortState, onSetFilter)}
    </div>
  ) : (
    <div className={classname}>{title}</div>
  );
}
